<script setup>
import { computed, ref } from 'vue';
import { getUnixTime } from 'date-fns';
import { useReminder } from 'dashboard/composables/useReminder';
import {
  REMINDER_PRESETS,
  CUSTOM_REMINDER_PRESET,
  getReminderTimestamp,
  formatReminderTimestamp,
  toDateTimeLocalValue,
} from 'dashboard/helper/reminderHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const { reminder, isSaving, setReminder } = useReminder();

const DEFAULT_PRESET = 'tomorrow';
// A reminder in the past would fire on the next sweep, which is never what the
// agent meant. One minute is enough to make testing a real firing practical.
const MINIMUM_LEAD_MINUTES = 1;
// A reminder is a one-line prompt to a colleague, not a handover document.
const NOTE_MAX_LENGTH = 150;
const NOTE_COUNTER_THRESHOLD = 20;

const selectedPreset = ref(DEFAULT_PRESET);
const customTime = ref('');
const note = ref(reminder.value?.note ?? '');

const isEditing = computed(() => Boolean(reminder.value));
const isCustom = computed(
  () => selectedPreset.value === CUSTOM_REMINDER_PRESET
);

const minimumCustomTime = computed(() => {
  const earliest = new Date();
  earliest.setMinutes(earliest.getMinutes() + MINIMUM_LEAD_MINUTES);
  return toDateTimeLocalValue(earliest);
});

// Unix seconds, the same shape the snooze flow sends as snoozed_until.
const resolvedTimestamp = computed(() => {
  if (!isCustom.value) return getReminderTimestamp(selectedPreset.value);
  if (!customTime.value) return null;

  const parsed = new Date(customTime.value);
  if (Number.isNaN(parsed.getTime())) return null;
  return getUnixTime(parsed);
});

const isInFuture = computed(() => {
  if (!resolvedTimestamp.value) return false;
  return resolvedTimestamp.value * 1000 > Date.now();
});

const firesAt = computed(() =>
  formatReminderTimestamp(resolvedTimestamp.value)
);

const canSubmit = computed(
  () => Boolean(note.value.trim()) && isInFuture.value && !isSaving.value
);

const charactersLeft = computed(() => NOTE_MAX_LENGTH - note.value.length);

// Stays out of the way until the limit is close enough to matter.
const showCharacterCount = computed(
  () => charactersLeft.value <= NOTE_COUNTER_THRESHOLD
);

const selectPreset = preset => {
  selectedPreset.value = preset;
};

const onSubmit = async () => {
  if (!canSubmit.value) return;
  const saved = await setReminder({
    snoozedUntil: resolvedTimestamp.value,
    note: note.value.trim(),
  });
  if (saved) emit('close');
};
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3">
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="preset in REMINDER_PRESETS"
        :key="preset"
        type="button"
        class="px-2 py-1 text-xs border rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
        :class="{
          'bg-n-alpha-2 !text-n-slate-12 border-n-slate-6':
            selectedPreset === preset,
        }"
        @click="selectPreset(preset)"
      >
        {{ $t(`CONVERSATION.REMINDER.PRESETS.${preset.toUpperCase()}`) }}
      </button>
    </div>

    <input
      v-if="isCustom"
      v-model="customTime"
      type="datetime-local"
      :min="minimumCustomTime"
      class="w-full max-w-xs px-2 py-1.5 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
    />

    <!-- Always shows the resolved moment, so a preset label never has to be
    trusted and a custom time is confirmed before saving. -->
    <span v-if="firesAt && isInFuture" class="text-xs text-n-slate-11">
      {{ $t('CONVERSATION.REMINDER.FIRES_AT', { time: firesAt }) }}
    </span>
    <span v-else-if="isCustom && customTime" class="text-xs text-n-ruby-11">
      {{ $t('CONVERSATION.REMINDER.PAST_TIME') }}
    </span>

    <!-- Not autofocused: the panel opens on a tab, and stealing the caret makes
    the field look active before the agent has chosen to write in it. -->
    <div class="flex flex-col gap-1">
      <textarea
        v-model="note"
        rows="2"
        :maxlength="NOTE_MAX_LENGTH"
        class="w-full px-2 py-1.5 text-sm border rounded-lg resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12"
        :placeholder="$t('CONVERSATION.REMINDER.NOTE_PLACEHOLDER')"
        @keydown.enter.meta="onSubmit"
        @keydown.enter.ctrl="onSubmit"
        @keydown.esc="emit('close')"
      />
      <span
        v-if="showCharacterCount"
        class="text-xs ltr:text-right rtl:text-left text-n-slate-11"
      >
        {{
          $t('CONVERSATION.REMINDER.CHARACTERS_LEFT', { count: charactersLeft })
        }}
      </span>
    </div>

    <div class="flex justify-end gap-2">
      <NextButton
        faded
        slate
        sm
        :label="$t('CONVERSATION.REMINDER.CANCEL')"
        @click="emit('close')"
      />
      <NextButton
        sm
        :label="
          isEditing
            ? $t('CONVERSATION.REMINDER.UPDATE')
            : $t('CONVERSATION.REMINDER.SUBMIT')
        "
        :disabled="!canSubmit"
        :is-loading="isSaving"
        @click="onSubmit"
      />
    </div>
  </div>
</template>
